import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import type { ResolvedColumn } from '../../domain';
import type { SearchBase } from './use-search';
import type { DropdownKind, OperatorOption, Option } from './types';
import { buildFilter, columnKey } from './utils';

const DROPDOWN_BY_FILTER: Partial<Record<string, DropdownKind>> = {
    set: 'value',
    date: 'operator',
    number: 'operator',
};

export interface SearchActions<T> {
    selectColumn: (col: ResolvedColumn<T>) => void;
    selectOperator: (opt: OperatorOption) => void;
    selectValue: (opt: Option) => void;
    selectDate: (iso: string) => void;
    commitText: () => void;
    cancelPending: () => void;
}

export function useSearchActions<T>(base: SearchBase<T>): SearchActions<T> {
    const {
        adapter,
        timer,
        focusInput,
        pendingColumn,
        setPendingColumn,
        pendingOperator,
        setPendingOperator,
        inputValue,
        setInputValue,
        setDropdown,
    } = base;

    const reset = useCallback(() => {
        setPendingColumn(null);
        setPendingOperator(null);
        setInputValue('');
        setDropdown(null);
    }, [setPendingColumn, setPendingOperator, setInputValue, setDropdown]);

    const commit = useCallback(
        (
            col: ResolvedColumn<T>,
            value: string | string[],
            operator?: OperatorOption['value'],
        ) => {
            adapter.setFilter(
                columnKey(col),
                buildFilter(col, value, operator),
            );
            flushSync(reset);
            focusInput();
        },
        [adapter, reset, focusInput],
    );

    const cancelPending = useCallback(() => {
        flushSync(reset);
        focusInput();
    }, [reset, focusInput]);

    const selectColumn = useCallback(
        (col: ResolvedColumn<T>) => {
            clearTimeout(timer.current);
            flushSync(() => {
                setPendingColumn(col);
                setInputValue('');
                setDropdown(DROPDOWN_BY_FILTER[col.filter as string] ?? null);
            });
            focusInput();
        },
        [timer, setPendingColumn, setInputValue, setDropdown, focusInput],
    );

    const selectOperator = useCallback(
        (opt: OperatorOption) => {
            flushSync(() => {
                setPendingOperator(opt);
                setDropdown(pendingColumn?.filter === 'date' ? 'date' : null);
            });
            focusInput();
        },
        [pendingColumn, setPendingOperator, setDropdown, focusInput],
    );

    const selectValue = useCallback(
        (opt: Option) => {
            if (pendingColumn) commit(pendingColumn, [opt.value]);
        },
        [pendingColumn, commit],
    );

    const selectDate = useCallback(
        (iso: string) => {
            if (pendingColumn)
                commit(pendingColumn, iso, pendingOperator?.value);
        },
        [pendingColumn, pendingOperator, commit],
    );

    const commitText = useCallback(() => {
        if (!pendingColumn) {
            clearTimeout(timer.current);
            adapter.setSearch(inputValue);
            setInputValue('');
            setDropdown(null);
            return;
        }
        const value = inputValue.trim();
        if (!value) return;
        if (pendingColumn.filter === 'text') commit(pendingColumn, value);
        else if (pendingColumn.filter === 'number' && pendingOperator)
            commit(pendingColumn, value, pendingOperator.value);
    }, [
        pendingColumn,
        pendingOperator,
        inputValue,
        adapter,
        timer,
        setDropdown,
        commit,
    ]);

    return {
        selectColumn,
        selectOperator,
        selectValue,
        selectDate,
        commitText,
        cancelPending,
    };
}
